import moment from "moment";
import { compose } from "redux";
import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";

import withUserPerm from "services/withUserPerm";

import DatePicker from "components/DatePicker";

import PerformanceSummary from "components/dashboard/PerformanceSummary";
import SpendingAndConversion from "components/dashboard/SpendingAndConversion";

import "styles/Dashboard.css";

import useCurrency from "modules/utils/useCurrency";
import mainActions from "modules/main/actions";
import mainSelectors from "modules/main/selectors";
import formatMetricValue from "modules/utils/formatMetricValue";
import performanceAction from "modules/performance/actions";
import performanceSelectors from "modules/performance/selectors";
import useWindowSize from "../layout/useWindowSize";
import facebookActions from "modules/facebook/actions";

import Loading from "components/Loading";
import LoadingContainer from "components/LoadingContainer";
import TopBar from "../components/TopBar";
import styled from "styled-components";

const Dashboard = () => {
  const rmpToken = sessionStorage.getItem("rmp_token");
  const { REACT_APP_RMP_APP_ID, REACT_APP_FACEBOOK_RP_BUSINESS_ID } = process.env;
  const [t] = useTranslation();
  const dispatch = useDispatch();
  let history = useHistory();
  const isMobile = useWindowSize().width < 835;
  const yesterdayAsDefault = true;

  const [loadedSelfServiceSettings, setLoadedSelfServiceSettings] = useState(false);
  const [loading, setLoading] = useState(true);

  const currentAd = useSelector((state) => mainSelectors.currentFacebookAd(state));
  const fbDashboardList = useSelector((state) => performanceSelectors.fbdashboardList(state));
  const [selectedViewValue, setSelectedViewValue] = useState();
  const currentDashboard = useSelector((state) => mainSelectors.currentDashboardResult(state));
  const windowSize = useWindowSize();
  useEffect(() => {
    getFacebookSettings();
  }, []);

  useEffect(() => {
    if (loadedSelfServiceSettings === true) {
      checkAdsAccountConnection();
    }
  }, [currentAd, loadedSelfServiceSettings]);

  const getFacebookSettings = async () => {
    const facebookSettings = await dispatch(
      facebookActions.getFacebookSettings({
        token: rmpToken,
      }),
    );

    if (facebookSettings.result === 0 && facebookSettings.data[0] && facebookSettings.data[0].fb_ads_account_id) {
      const pixelAgencies = await dispatch(
        facebookActions.getFacebookPixelAgencies({
          token: rmpToken,
          ads_account_id: facebookSettings.data[0].fb_ads_account_id,
          user_access_token: "",
          pixels_id: facebookSettings.data[0].fb_ads_pixel_code_id,
        }),
      );

      if (pixelAgencies.result === 0 && facebookSettings.data[0].setup_status === "completed") {
        dispatch(
          mainActions.SetFacebookAd({
            account_name: facebookSettings.data[0].fb_ads_account_name,
            ads_account_id: facebookSettings.data[0].fb_ads_account_id,
            type: "fb",
            currency_code: "THB",
            create_time: null,
            update_time: null,
          }),
        );

        setLoadedSelfServiceSettings(true);
      } else if (
        pixelAgencies.result !== 0 &&
        pixelAgencies.message === "NO_RP_AGENCY" &&
        facebookSettings.data[0].fb_ads_business_id === REACT_APP_FACEBOOK_RP_BUSINESS_ID
      ) {
        dispatch(
          mainActions.SetFacebookAd({
            account_name: facebookSettings.data[0].fb_ads_account_name,
            ads_account_id: facebookSettings.data[0].fb_ads_account_id,
            type: "fb",
            currency_code: "THB",
            create_time: null,
            update_time: null,
          }),
        );

        setLoadedSelfServiceSettings(true);
      } else {
        history.push("/no-connected-ads");
      }
    } else if (facebookSettings.result === 0 && facebookSettings.data.length === 0) {
      history.push("/no-connected-ads");
    }
  };

  const checkAdsAccountConnection = () => {
    if (!currentAd || currentAd === "" || !currentAd.ads_account_id) {
      history.push("/no-connected-ads");
    }
  };

  const getFacebookDashboardData = async () => {
    await dispatch(performanceAction.GetFBDashboardList(currentAd.ads_account_id, selectedViewValue.key));
    setLoading(false);
  };

  const changeDateFilter = (value) => {
    setLoading(true);
    setSelectedViewValue(value);
  };

  useEffect(() => {
    if (selectedViewValue && selectedViewValue.key && currentAd && currentAd.ads_account_id) {
      getFacebookDashboardData();
    }
  }, [selectedViewValue, currentAd.ads_account_id, dispatch]);

  let performanceData = fbDashboardList || {};

  const isPositiveNumber = (data) => {
    var value = data;
    if (typeof data !== "number") {
      value = data.replace(",", "");
      value = parseFloat(value);
    }
    return value > 0 ? "+" + data : data;
  };

  const performanceInfo = [
    {
      title: t("__clicks"),
      value: performanceData.clicks ? formatMetricValue("click", performanceData.clicks.replace(/\,/g, "")) : 0,
      change: !performanceData.sub_clicks
        ? "-"
        : performanceData.sub_clicks === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("click", performanceData.sub_clicks)) + "%",
      // icon: "icon-click"
    },
    {
      title: t("__impressions"),
      value: performanceData.impressions
        ? formatMetricValue("impr", performanceData.impressions.replace(/\,/g, ""))
        : 0,
      change: !performanceData.sub_impressions
        ? "-"
        : performanceData.sub_impressions === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("impr", performanceData.sub_impressions)) + "%",
      // icon: "icon-eye"
    },
    {
      title: t("__ctr"),
      value: formatMetricValue("ctr", performanceData.ctr),
      change: !performanceData.sub_ctr
        ? "-"
        : performanceData.sub_ctr === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("ctr", performanceData.sub_ctr)),
      // icon: "icon-ctr"
    },
    {
      title: t("__avg_cpc"),
      value: formatMetricValue("cpc", performanceData.cpc),
      change: !performanceData.sub_cpc
        ? "-"
        : performanceData.sub_cpc === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("cpc", performanceData.sub_cpc)) + "%",
      // icon: "icon-click-coins",
      unit: useCurrency(),
    },
  ];

  const spendingConversionInfo = [
    {
      title: t("__dashboard_spending"),
      value: formatMetricValue("cost", performanceData.spend),
      unit: useCurrency(),
      change: !performanceData.sub_spend
        ? "-"
        : performanceData.sub_spend === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("cost", performanceData.sub_spend)) + "%",
      // icon: "icon-payment"
    },
    {
      title: t("__dashboard_conversion"),
      value: formatMetricValue("conversion", performanceData.conversions),
      change: !performanceData.sub_conversions
        ? "-"
        : performanceData.sub_conversions === "N/A"
          ? "N/A"
          : isPositiveNumber(formatMetricValue("conversion", performanceData.sub_conversions)) + "%",
      // icon: "icon-retweet"
    },
  ];

  if (loadedSelfServiceSettings === true) {
    return (
      <div>
        <StyledContainerTop useWindowSize={windowSize}>
          <StyledContainerTopBar useWindowSize={windowSize}>
            <TopBar title={t("__dashboard")} />
          </StyledContainerTopBar>
          <DatePicker
            key={moment.locale() + " " + yesterdayAsDefault}
            onSelected={(value) => changeDateFilter(value)}
            defaultIndex={yesterdayAsDefault ? 1 : 0}
          />
        </StyledContainerTop>
        {loading === true ? (
          <Loading />
        ) : (
          <>
            <PerformanceSummary performanceInfo={performanceInfo} />
            <SpendingAndConversion spendingConversionInfo={spendingConversionInfo} />
          </>
        )}
      </div>
    );
  } else {
    return <>{isMobile === true ? <Loading /> : <LoadingContainer show={true} top="55" />}</>;
  }
};

export default compose(withUserPerm())(Dashboard);

const StyledContainerTop = styled.div.attrs((props) => ({
  display: props.useWindowSize.width < 835 ? "block" : "flex",
}))`
  display: ${(props) => props.display};
  justify-content: space-between;
  margin-bottom: 24px;
  display:;
`;

const StyledContainerTopBar = styled.div.attrs((props) => ({
  display: props.useWindowSize.width < 835 ? "none" : "block",
}))`
  display: ${(props) => props.display};
`;
